import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ListItem } from '../../../models/api/core/list/list-item';
import { FilterBase } from '../../../models/api/reports/all-filters-response';
import { ClaimViewResponse } from '../../../models/api/view/claim-view/claim-view-response';
import { ClientListService } from '../../../services/client-list/client-list.service';
import { ExportService } from '../../../services/export/export.service';
import { ClaimViewColumnService } from '../../../services/view/claim-view-column.service';
import { WatchlistService } from '../../../services/view/watchlist.service';
import { ClaimExportMapper } from '../../../utilities/claim-export-mapper';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ExportFormat } from '../../core/buttons/export-button.component';
import { ClientSelectorComponent } from '../../core/controls/client-selector/client-selector.component';
import { ApiErrorLabelComponent } from '../../core/labels/api-error-label.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../core/panels/loading-panel.component';
import { MyFiltersComponent } from '../../reports/my-filter/my-filters.component';
import { ClaimViewComponent } from '../claim-view/claim-view.component';

@Component({
  selector: 'app-filter-search',
  imports: [
    CommonModule,
    LoadingPanelComponent,
    RouterModule,
    EmptyStateComponent,
    ClaimViewComponent,
    ApiErrorLabelComponent,
    ActionRowComponent,
    ButtonComponent,
    MyFiltersComponent,
    ClientSelectorComponent,
  ],
  template: `
    <div class="tw-w-full tw-py-6">
      <app-my-filters
        (search)="search($event)"
        [searching]="state === 'Loading'"
        [showSearch]="true"
        [showHelpText]="false"
        [headerTemplate]="clientSelector"
        [searchClient]="client.value"
        [showPaginator]="false"
        scrollHeight="576px"
        redirectTo="view?tab=filter"
      />
    </div>
    <ng-template #clientSelector>
      <app-client-selector
        [control]="client"
        [showHelpText]="false"
      />
    </ng-template>

    @if (filter) {
      <app-action-row>
        <div left1>
          <h2 class="-tw-text-[22px]">
            <div
              class="tw-font-bold tw-text-[var(--sed-black-60-color)] tw-text-base"
            >
              {{ searchClientLabel }}
            </div>
            {{ searchLabel }}
          </h2>
        </div>

        <app-button right1 emphasis="Low" label="Clear" (click)="clear()" />
      </app-action-row>
    }

    @if (client.value) {
      <app-loading-panel
        id="filter-search-results"
        [state]="state"
        loadingStyle="Skeleton"
        [loadingHeight]="450"
      >
        <ng-template #init>
          <app-empty-state
            icon="fa-light fa-search"
            size="120px"
            label="Search results"
            message="Search results will appear here"
            height="400px"
          />
        </ng-template>
        <ng-template #empty>
          <app-api-error-label [error]="error" />
          <app-empty-state
            icon="fa-light fa-magnifying-glass"
            size="120px"
            label="No results found"
            message="Try searching with different filter"
            height="400px"
          />
        </ng-template>
        <ng-template #loaded>
          <app-claim-view
            [claimView]="claimView"
            [columnView]="claimViewColumnService.columnView()"
            [persistPaginationSettings]="true"
            [tableId]="'search-results'"
            (export)="onExport($event)"
            (columnsUpdated)="onColumnsUpdated()"
          />
        </ng-template>
      </app-loading-panel>
    }
  `,
  styles: ``,
})
export class FilterSearchComponent extends BaseComponent implements OnInit {
  exportService = inject(ExportService);
  claimViewColumnService = inject(ClaimViewColumnService);
  private watchlist = inject(WatchlistService);
  private datePipe = inject(DatePipe);

  private clientListService = inject(ClientListService);

  claimView: ClaimViewResponse;

  state: LoadingState = 'Init';

  error: any;

  filter: FilterBase | null;

  searchLabel: string;

  searchClientLabel: string;

  client = new FormControl<ListItem>(null);

  ngOnInit(): void {
    this.dispose.subs =
      this.claimViewColumnService.columnConfigRefreshed$.subscribe(() => {
        if (this.filter) {
          this.search(this.filter);
        }
      });

    this.dispose.subs = this.client.valueChanges.subscribe((client) => {
      this.clear();
    });

    this.dispose.subs = this.activatedRoute.queryParams.subscribe(
      async (params) => {
        const contractNumber = params['contractNumber'];
        if (contractNumber) {
          const clients = this.clientListService.clients().length
            ? this.clientListService.clients()
            : await this.clientListService.load();
          const match = clients.find((c) => c.value === contractNumber);
          if (match && this.client.value?.value !== match.value) {
            this.client.setValue(match);
          }
        }
      },
    );
  }

  search(filter: FilterBase) {
    this.state = 'Loading';
    this.error = null;
    this.searchLabel = filter.filterName;
    if (filter.owner === 'SYSTEM') {
      this.searchClientLabel = this.client.value?.description;
    } else {
      this.searchClientLabel = `${filter.contractNumber} - ${filter.clientName}`;
    }

    this.filter = filter;

    setTimeout(() => {
      const el = document.getElementById('filter-search-results');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    const contractNumber =
      filter.owner === 'SYSTEM'
        ? this.client.value?.value
        : filter.contractNumber;

    Promise.all([
      this.api.search.filterSearch(
        filter.filterId,
        filter.system,
        filter.model,
        contractNumber,
      ),
      this.claimViewColumnService.load(),
    ])
      .then((value) => {
        this.claimView = value[0];
        this.state = this.claimView.rows.length ? 'Loaded' : 'Empty';
      })
      .catch((error) => {
        this.error = error;
        this.state = 'Empty';
        throw error;
      })
      .finally(() => {
        this.searchLabel = filter.filterName;
      });
  }

  async clear() {
    this.state = 'Init';
    this.claimView = null;
    this.filter = null;
  }

  onColumnsUpdated() {
    if (this.filter) {
      this.search(this.filter);
    }
  }

  async onExport(format: ExportFormat) {
    try {
      const claimRows = this.claimView?.rows ?? [];
      const watchedClaims = this.watchlist.claims()?.rows ?? [];
      const rows = claimRows.map((row) => ({
        ...row,
        watchStatus: watchedClaims.some(
          (w) =>
            w.recordId === row.recordId && w.recordSource === row.recordSource,
        )
          ? 'Y'
          : 'N',
      }));
      const claimColumns =
        this.claimViewColumnService.columnView()?.columns ?? [];
      const columns = [
        {
          propertyName: 'watchStatus',
          headerText: 'Watchlist',
        },
        ...claimColumns.filter((col) => col.propertyName !== 'DateAccessed'),
      ];
      const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
      const filename = `ClaimSearch_${formattedDate}.${format}`;

      if (format === 'csv') {
        const csvData = ClaimExportMapper.toCsvExport(
          rows,
          columns,
          'ClaimSearch',
        );
        await this.exportService.exportCsv(csvData, filename);
      } else if (format === 'xlsx') {
        const excelData = ClaimExportMapper.toExcelExport(
          rows,
          columns,
          'ClaimSearch',
          'OmniInsights Export',
        );
        await this.exportService.exportExcel(excelData, filename);
      }
    } catch (error) {
      this.error = error;
    }
  }
}
